/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { FC, useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import moment from 'moment';
import $script from 'scriptjs';
import {
  ArrowRound,
  Calendar,
  ChecklistClearSmall,
  ChecklistCriticalSmall,
  ChecklistDetails,
  ChecklistIssuesSmall,
  ChecklistNotReportedSmall,
} from '@assets/svg/icons';
import cn from 'classnames';
import 'dhtmlx-scheduler';
import 'dhtmlx-scheduler/codebase/dhtmlxscheduler_material.css';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_year_view';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_tooltip';
import '../../../../vendors/dhtmlxScheduler/dhtmlxscheduler_timeline';
import '../../../../vendors/dhtmlxScheduler/dhtmlxscheduler_minical';

import { IVehicle, IVehiclesChecklist, IWindowWithScheduler } from '@common/interfaces';
import { getUnavailability, examineChecklistStatus } from '@pages/VehiclesChecklistPage/utils';

import css from './styles.module.scss';

$script('https://export.dhtmlx.com/scheduler/api.js', 'bundle');

const { scheduler } = window as unknown as IWindowWithScheduler;

const TIMELINE_VIEW = 'week_timeline';
const VEHICLE_CELL_HEIGHT = 115;
const VEHICLE_CELL_WIDTH = 310;
const TOP_SCALE_HEIGHT = 40;
const HEADER_HEIGHT = 60;

export interface IVehicleChecklistScheduler extends IVehicle {
  key: string;
  criticalIssue?: {
    start: string;
    end: string;
  };
}

interface IVehiclesChecklistCalendarProps {
  vehicles: IVehicleChecklistScheduler[];
  checklists: IVehiclesChecklist[];
  schedulerDate: Date;
  onDateChange: (newDate: Date) => void;
  getChecklistDetails: (id: string) => void;
  goToChecklistDetailsPage: (id: string) => void;
}

const VehiclesChecklistCalendar: FC<IVehiclesChecklistCalendarProps> = ({
  vehicles,
  checklists,
  schedulerDate,
  onDateChange,
  getChecklistDetails,
  goToChecklistDetailsPage,
}) => {
  let schedulerContainer: HTMLElement;

  const handleShowCalendar = (): void => {
    if (scheduler.isCalendarVisible()) {
      scheduler.destroyCalendar();
    } else {
      scheduler.renderCalendar({
        position: 'dhx_minical_icon',
        date: scheduler.getState().date,
        navigation: true,
        handler: (date: Date) => {
          scheduler.setCurrentView(date);
          scheduler.destroyCalendar();
        },
      });
    }
  };

  useEffect(() => {
    scheduler.skin = 'material';
    scheduler.xy.scale_height = TOP_SCALE_HEIGHT;
    scheduler.xy.margin_left = 100;
    scheduler.config.details_on_dblclick = true;
    scheduler.config.details_on_create = true;
    scheduler.config.select = false;
    scheduler.config.drag_create = false;
    scheduler.config.drag_move = false;
    scheduler.config.drag_resize = false;
    scheduler.config.auto_end_date = true;
    scheduler.config.event_duration = 60;
    scheduler.config.collision_limit = 10;
    scheduler.config.show_loading = true;

    const currentVehicles = scheduler.serverList('vehicles', vehicles);

    scheduler.createTimelineView({
      name: TIMELINE_VIEW,
      render: 'bar',
      x_unit: 'day',
      x_date: '%D <br/> %j',
      x_step: 1,
      x_size: 7,
      x_length: 7,
      y_property: 'vehicle',
      section_autoheight: false,
      event_dy: 25,
      event_min_dy: 25,
      dy: VEHICLE_CELL_HEIGHT,
      dx: VEHICLE_CELL_WIDTH,
      y_unit: currentVehicles,
    });

    scheduler.attachEvent('onTemplatesReady', () => {
      scheduler.templates.event_text = () => '';
      scheduler.templates.event_bar_date = () => '';
      scheduler.templates.tooltip_text = () => '';

      scheduler.templates.week_date = (start: Date, end: Date) => {
        if (start.getMonth() !== end.getMonth()) {
          return `${scheduler.templates.day_date(start)} &ndash; ${scheduler.templates.day_date(
            scheduler.date.add(end, -1, 'day'),
          )}`;
        }

        return `${start.getDate()} &ndash; ${end.getDate() - 1} ${moment(start).format(
          'MMMM YYYY',
        )}`;
      };

      scheduler.templates.week_timeline_scale_label = (
        _key: string,
        _label: string,
        vehicle: IVehicleChecklistScheduler,
      ) =>
        renderToString(
          <section className={css.vehicle}>
            <div className={css['vehicle-image']}>
              <img src={vehicle.model?.image ?? undefined} alt={vehicle.plateNumber} />
              <div className={css.protection}>
                {vehicle.protection === 'soft' ? 'Soft' : 'Armoured'}
              </div>
            </div>
            <div className={css['vehicle-content']}>
              <h3>
                {vehicle.model?.name} <br /> {vehicle.plateNumber}
              </h3>
              {vehicle.currentUnavailability || vehicle.criticalIssue ? (
                <h4
                  className={cn({
                    [css['unavailability--issues']]: vehicle.currentUnavailability,
                    [css['unavailability--critical']]: vehicle.criticalIssue,
                  })}
                >
                  {getUnavailability(vehicle)}
                </h4>
              ) : null}
            </div>
          </section>,
        );

      scheduler.templates.event_bar_text = (
        _start: string,
        _end: string,
        checklist: IVehiclesChecklist,
      ) => {
        let content: JSX.Element;
        const { status, statusName } = examineChecklistStatus(checklist);

        if (status === 'not-used') {
          content = <h3>{statusName}</h3>;
        } else {
          const iconsMapper: Record<
            string,
            React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>
          > = {
            clear: ChecklistClearSmall,
            issues: ChecklistIssuesSmall,
            critical: ChecklistCriticalSmall,
            'not-reported': ChecklistNotReportedSmall,
          };

          const ChecklistIcon = iconsMapper[status];
          const driverFullName = `${checklist.driver_first_name ?? '\u00A0'} ${
            checklist.driver_last_name ?? '\u00A0'
          }`;

          content = (
            <>
              <div
                className={cn(css['checklist-header'], {
                  [css[`checklist-header--${status}`]]: true,
                })}
              >
                {statusName}
              </div>
              <div className={css['checklist-content']}>
                <h4>
                  {driverFullName.length > 18
                    ? `${driverFullName.substring(0, 18)}...`
                    : driverFullName}
                </h4>
                <div className={css['checklist-status']}>
                  <div
                    className={cn(css['status-icon'], {
                      [css[`status-icon--${status}`]]: true,
                    })}
                  >
                    <ChecklistIcon />
                  </div>
                  <ChecklistDetails className={css['checklist-details']} />
                </div>
              </div>
            </>
          );
        }

        return renderToString(
          <figure
            className={cn(css.checklist, { [css[`checklist--${status}`]]: true })}
            data-testid={`checklist-${status}`}
          >
            {content}
            {status === 'not-used' ? <div className={css.blur} /> : null}
          </figure>,
        );
      };
    });

    scheduler.attachEvent(
      'onBeforeViewChange',
      (oldMode: string, oldDate: string, newMode: string, newDate: string) => {
        if (oldMode === newMode && oldDate === newDate) {
          return false;
        }

        return true;
      },
    );

    scheduler.attachEvent('onViewChange', (_: unknown, new_date: Date) => {
      const startOfWeek = moment(new_date).startOf('week').toDate();
      scheduler.updateView(startOfWeek);
      onDateChange(startOfWeek);
    });

    scheduler.attachEvent('onClick', (id: number) => {
      const checklist = scheduler.getEvent(id) as IVehiclesChecklist;
      const { status } = examineChecklistStatus(checklist);

      if (status === 'issues' || status === 'critical') {
        getChecklistDetails(checklist.id);
      } else {
        goToChecklistDetailsPage(checklist.id);
      }
    });

    scheduler.attachEvent('onDblClick', () => false);

    scheduler.init(
      schedulerContainer,
      moment(schedulerDate).startOf('week').toDate(),
      TIMELINE_VIEW,
    );
  }, [vehicles]);

  useEffect(() => {
    scheduler.updateCollection('vehicles', vehicles);
  }, [vehicles]);

  useEffect(() => {
    scheduler.clearAll();
    scheduler.parse(checklists);
  }, [checklists]);

  const schedulerHeight = HEADER_HEIGHT + VEHICLE_CELL_HEIGHT * vehicles.length + 15;

  return (
    <div
      className={cn('dhx_cal_container', 'dhx_cal_checklist_scheduler')}
      style={{ height: schedulerHeight }}
      ref={(input) => {
        if (input) schedulerContainer = input;
      }}
    >
      <div className="dhx_cal_navline">
        <div className={css.calendar}>
          <div className="dhx_cal_date" />
          <div className="dhx_minical_icon" id="dhx_minical_icon" onClick={handleShowCalendar}>
            <Calendar />
          </div>
        </div>
        <div className="dhx_cal_prev_button">
          <ArrowRound style={{ transform: 'rotateZ(90deg)' }} />
        </div>
        <div className="dhx_cal_next_button">
          <ArrowRound style={{ transform: 'rotateZ(-90deg)' }} />
        </div>
      </div>
      <div className="dhx_cal_header" />
      <div className="dhx_cal_data" />
    </div>
  );
};

export default VehiclesChecklistCalendar;
